import { GridControlled } from '@casops/components/tables/grid/GridControlled';
import { RegisteredAssetBasicModel } from '@casops/store/apis/registeredAssetsApi';
import { ReactElement, useEffect, useMemo } from 'react';
import styles from './AssetTracksTable.module.scss';
import { lockColumn } from '@casops/components/tables/grid/columnDefs';
import AssetGroupCellRenderer from '@casops/components/tables/cells/asset-group/AssetGroupCellRenderer';
import { AssetNameCellRenderer } from '@casops/components/tables/cells/asset-name/AssetNameCellRenderer';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { getObjectTypeFromEnum } from '@casops/shared/utils/translationHelpers';
import { ObjectType } from '@casops/store/apis/commonModels';
import { useAssetGroupIdFormatter } from '@casops/components/tables/formatters/idFormatters';
import { useTranslation } from 'react-i18next';
import SearchBox from '@casops/components/inputs/search-box/SearchBox';
import classNames from 'classnames';
import { useController, UseControllerProps } from 'react-hook-form';
import { AssetTracksFormModel } from '../form/AssetTracksForm';
import { useAppDispatch, useAppSelector } from '@casops/store/hooks';
import { resetTable, selectGridData } from '@casops/store/slices/gridSlice';
import { GridState } from '@dmc/ui/tables';
import { MAX_ASSET_TRACKS } from '@casops/store/slices/assetTracksSlice';

type Model = RegisteredAssetBasicModel;

const sliceKey = 'AssetTracksForm';

const defaultColDef: ColDef<Model> = {
  ...lockColumn,
  sortable: true,
};

interface AssetTracksTableProps {
  control: UseControllerProps<AssetTracksFormModel>['control'];
  assets: RegisteredAssetBasicModel[] | undefined;
}

export const AssetTracksTable = ({ control, assets }: AssetTracksTableProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const assetGroupIdFormatter = useAssetGroupIdFormatter();

  const columnDefs = useMemo<ColDef<Model>[]>(
    () => [
      {
        field: 'n',
        headerName: t('Fields.AssetName'),
        minWidth: 110,
        maxWidth: 130,
        cellRenderer: AssetNameCellRenderer,
      },
      {
        field: 'obj',
        headerName: t('Fields.ObjectType'),
        minWidth: 150,
        valueGetter: ({ data }: ValueGetterParams<Model>): string =>
          getObjectTypeFromEnum(t, data?.obj ?? ObjectType.Unknown),
      },
      {
        field: 'ags',
        headerName: t('Fields.AssetGroup'),
        valueFormatter: assetGroupIdFormatter<Model, 'ags'>, // Searchable
        cellRenderer: AssetGroupCellRenderer,
        minWidth: 150,
      },
    ],
    [t, assetGroupIdFormatter],
  );

  // RTK entity adapters incorrectly assert that the state is always defined for any ID that is provided.
  const gridState = useAppSelector(state => selectGridData(state, sliceKey)) as
    | GridState
    | undefined;

  const selectedRows = useMemo<string[]>(
    () => gridState?.selectedRows ?? [],
    [gridState?.selectedRows],
  );

  const { field, fieldState } = useController({
    control,
    name: 'assetIds',
  });

  // Update the 'assetIds' field when the table row selection changes.
  useEffect(() => {
    field.onBlur();
    field.onChange(selectedRows.map(Number));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  // Cleanup the table for next use.
  useEffect(() => {
    return () => {
      dispatch(resetTable(sliceKey));
    };
  }, [dispatch]);

  const errorMessage = fieldState.error ? fieldState.error.message : undefined;

  const tracksCount = useAppSelector(state => state.assetTracks.trackIds.length);

  let fieldMessage = errorMessage;

  if (!errorMessage) {
    const formSelectionLimit = MAX_ASSET_TRACKS - tracksCount;
    const remaining = formSelectionLimit - selectedRows.length;
    const countHelper = t('Validation.AssetTrackSelectLimit', {
      count: formSelectionLimit,
      remaining,
    });
    const limitReachedHelper = t('Validation.AssetTrackLimitReached', {
      count: formSelectionLimit,
    });
    fieldMessage = remaining > 0 ? countHelper : limitReachedHelper;
  }

  return (
    <div className={styles.container}>
      <div
        className={classNames(styles.tableField, { [styles['tableField--error']]: errorMessage })}
      >
        <SearchBox sliceKey={sliceKey} />
        <GridControlled<Model>
          sliceKey={sliceKey}
          rowData={assets}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelectMode="multiRow"
        />
      </div>
      <div className={classNames(styles.message, { [styles['message--error']]: errorMessage })}>
        {fieldMessage}
      </div>
    </div>
  );
};

export default AssetTracksTable;
